// src/Publish.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { baseUrl } from '../api';
import {
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Box,
  Alert,
  Stack,
  FormHelperText,
  Checkbox
} from '@mui/material';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { AuthContext } from '../context/AuthContext';
import { useAxiosAuth }  from "../hooks/useAxiosAuth";

export default function PublishToLMS() {
  const { user, accessToken, signInWithGoogle } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);

  // Shared
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);

  // Tab: Quiz → Download (local)
  const [downloadQuizzes, setDownloadQuizzes] = useState([]);
  const [selectedDownloadQuizId, setSelectedDownloadQuizId] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  const axiosInstance = useAxiosAuth();

  const [dlQuizSpec, setDlQuizSpec] = useState(null); // server spec for selected download quiz (name, spec.questions)
  const [pkgId, setPkgId] = useState('');
  const [pkgTitle, setPkgTitle] = useState('');
  const [pkgProfile, setPkgProfile] = useState('stem.v1');

  const [sectionsCfg, setSectionsCfg] = useState([
    { id: 'main', title: 'Main', modules: [{ id: 'main-m1', time_limit_sec: 3600 }] }
  ]);

  // Navigation
  const [navCfg, setNavCfg] = useState({ allow_back: true, module_locked: false, subject_tabs: false });

  // Scoring: { [sectionId]: { [partKey]: { correct, wrong, unattempted, attempt_limit?, offered? } } }
  const [scoringCfg, setScoringCfg] = useState({});

  // Proctor & meta
  const [proctorCfg, setProctorCfg] = useState({ screenshot: false, camera: false });
  const [metaVersion, setMetaVersion] = useState('gen-1');

  // Question mapping (aligned with spec.questions index) – used for single-quiz path
  const [qAssign, setQAssign] = useState([]); // [{section_id, module_id, part, points?}]
  const PART_DEFAULT = 'A';

  // ── Composition modes ───────────────────────────────────────────────────────────
  // 'single' (Single quiz, single module mapping)
  // 'multi'  (Multi modules, each bound to a quiz-draft)
  // 'adaptive' (SAT-style: variants + by_score routing)
  const [compositionMode, setCompositionMode] = useState('single');

  // Per-course quiz cache
  const [quizzesByCourse, setQuizzesByCourse] = useState({}); // { [courseId]: Quiz[] }

  // Per-module selection: key = `${sectionId}::${moduleId}`
  const [moduleDrafts, setModuleDrafts] = useState({}); // { key: { course_id, quiz_id, part? } }

  // Adaptive config per module (placeholder): key = `${sectionId}::${moduleId}`
  // adaptiveCfg[key] = { enabled, threshold, variants: [{ id, time_limit_sec }] }
  const [adaptiveCfg, setAdaptiveCfg] = useState({});

  // Draft selection per variant id (unique across exam)
  // variantDrafts[variantId] = { course_id, quiz_id, part? }
  const [variantDrafts, setVariantDrafts] = useState({});

  const moduleKey = (secId, modId) => `${secId}::${modId}`;

  // load courses once
  useEffect(() => {
    (async () => {
      try {
        const resp = await axiosInstance.get('/courses/');
        setCourses(resp.data);
      } catch {
        setError('Unable to load courses.');
      }
    })();
  }, []);

  // ── Fetchers ────────────────────────────────────────────────────────────────────
  const fetchDownloadQuizzes = async () => {
    setError('');
    if (!selectedCourse) {
      setDownloadQuizzes([]);
      return;
    }
    try {
      const resp = await axiosInstance.get(`/exams/quizzes/${selectedCourse}`);
      setDownloadQuizzes(resp.data);
    } catch {
      setError('Unable to load quizzes.');
    }
  };

  const fetchQuizzesForCourse = async (courseId) => {
    if (!courseId) return;
    try {
      const resp = await axiosInstance.get(`/exams/quizzes/${courseId}`);
      setQuizzesByCourse(prev => ({ ...prev, [courseId]: resp.data || [] }));
    } catch {
      // ignore soft errors; keep cache as-is
    }
  };

  const ensureUniqueIdFactory = () => {
    const seen = new Set();
    return (idBase) => {
      if (!idBase) return null;
      let id = idBase;
      let n = 1;
      while (seen.has(id)) {
        id = `${idBase}-${++n}`;
      }
      seen.add(id);
      return id;
    };
  };

  // ── Download handler ────────────────────────────────────────────────────────────
  const handleDownloadQuiz = async () => {
    setError('');

    // Validate minimal policy
    if (!sectionsCfg.length || !sectionsCfg[0].modules?.length) {
      setError('Please add at least one section and one module.');
      return;
    }

    // Build policy object (inject variants/route for adaptive placeholders only in 'adaptive' mode)
    const sectionsPolicy = sectionsCfg.map(s => {
      const mods = (s.modules || []).map(m => {
        const key = moduleKey(s.id, m.id);
        const ac = adaptiveCfg[key];

        if (compositionMode === 'adaptive' && ac?.enabled && Array.isArray(ac.variants) && ac.variants.length >= 2) {
          const v0 = (ac.variants[0]?.id || '').trim();
          const v1 = (ac.variants[1]?.id || '').trim();
          const v2 = (ac.variants[2]?.id || '').trim();
          const route = v0 && v1 ? {
            by_score: {
              threshold: Number(ac.threshold) || 0,
              lte: v0,
              gt: v1,
              ...(v2 ? { default: v2 } : {})
            }
          } : undefined;

          return {
            id: m.id.trim(),
            time_limit_sec: Number(m.time_limit_sec) || 0,
            variants: ac.variants.map(v => ({
              id: (v.id || '').trim(),
              time_limit_sec: Number(v.time_limit_sec) || 0
            })),
            ...(route ? { route } : {})
          };
        }

        // Non-adaptive module (sequential)
        return { id: m.id.trim(), time_limit_sec: Number(m.time_limit_sec) || 0 };
      });

      return { id: s.id.trim(), title: s.title.trim(), modules: mods };
    });

    const policy = {
      sections: sectionsPolicy,
      navigation: navCfg,
      scoring: scoringCfg,
      proctor: proctorCfg,
      meta: { version: metaVersion || 'gen-1' },
    };

    let questions = [];

    if (compositionMode === 'single') {
      // — Single-quiz path —
      if (!selectedCourse) {
        setError('Please select a course.');
        return;
      }
      if (!selectedDownloadQuizId) {
        setError('Please select a quiz.');
        return;
      }
      if (!dlQuizSpec?.spec?.questions?.length) {
        setError('Selected quiz has no questions.');
        return;
      }
      const qlist = dlQuizSpec.spec.questions;
      questions = qlist.map((q, i) => {
        const assign = qAssign[i] || {};
        const base = {
          ...q,
          section_id: assign.section_id || sectionsCfg[0].id,
          module_id: assign.module_id || sectionsCfg[0].modules[0].id,
        };
        if (assign.part) base.part = assign.part;
        if (assign.points !== undefined && assign.points !== null && assign.points !== '') {
          base.points = Number(assign.points);
        }
        return base;
      });
    } else {
      // — Multi or Adaptive path —
      setDownloading(true);
      try {
        const ensureUniqueId = ensureUniqueIdFactory();

        // Build fetch plan
        const toFetch = []; // { targetModuleId, sectionId, part, quizId }

        sectionsCfg.forEach(sec => {
          (sec.modules || []).forEach(mod => {
            const key = moduleKey(sec.id, mod.id);
            const ac = adaptiveCfg[key];

            if (compositionMode === 'adaptive' && ac?.enabled && Array.isArray(ac.variants) && ac.variants.length >= 2) {
              // Adaptive: map per-variant
              ac.variants.forEach(v => {
                const vid = (v.id || '').trim();
                const vd = variantDrafts[vid] || {};
                if (vid && vd.quiz_id) {
                  toFetch.push({
                    targetModuleId: vid,  // variant id
                    sectionId: sec.id,
                    part: vd.part || PART_DEFAULT,
                    quizId: vd.quiz_id
                  });
                }
              });
            } else {
              // Multi: regular per-module binding
              const bind = moduleDrafts[key];
              if (bind?.quiz_id) {
                toFetch.push({
                  targetModuleId: mod.id,
                  sectionId: sec.id,
                  part: bind.part || PART_DEFAULT,
                  quizId: bind.quiz_id
                });
              }
            }
          });
        });

        if (!toFetch.length) {
          setError('No module/variant has a selected quiz. Choose drafts or switch to Single mode.');
          setDownloading(false);
          return;
        }

        // Fetch each quiz spec once (dedupe by quizId)
        const uniqQuizIds = Array.from(new Set(toFetch.map(t => t.quizId)));
        const quizSpecMap = {};
        await Promise.all(
          uniqQuizIds.map(async (qid) => {
            const resp = await axiosInstance.get(`/exams/quiz/${qid}`);
            quizSpecMap[qid] = resp.data || {};
          })
        );

        // Assemble questions
        const collected = [];
        for (const t of toFetch) {
          const spec = quizSpecMap[t.quizId]?.spec || {};
          const qs = Array.isArray(spec.questions) ? spec.questions : [];
          qs.forEach((q, idx) => {
            const id = ensureUniqueId(q.id || `q-${t.quizId}-${idx + 1}`);
            collected.push({
              ...q,
              id,
              section_id: t.sectionId,
              module_id: t.targetModuleId,  // module id or variant id
              part: t.part
            });
          });
        }

        questions = collected;
      } catch (e) {
        setError('Failed to gather questions for modules/variants. Please verify selected drafts.');
        setDownloading(false);
        return;
      }
      setDownloading(false);
    }

    // Final package
    const pkg = {
      id: (pkgId || 'exam').trim(),
      title: (pkgTitle || dlQuizSpec?.name || 'Exam').trim(),
      profile: (pkgProfile || 'stem.v1').trim(),
      policy,
      questions,
    };

    // Download
    const fname = `${pkg.id || 'exam'}.json`;
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // ── Mutators ────────────────────────────────────────────────────────────────────
  const updateSection = (i, updater) => {
    setSectionsCfg(prev => {
      const next = [...prev];
      next[i] = updater({ ...next[i] });
      return next;
    });
  };
  const addSection = () => setSectionsCfg(prev => [...prev, { id: `sec-${prev.length+1}`, title: `Section ${prev.length+1}`, modules: [{ id: `sec${prev.length+1}-m1`, time_limit_sec: 3600 }] }]);
  const removeSection = (i) => setSectionsCfg(prev => prev.filter((_, idx) => idx !== i));
  
  const addModule = (secIdx) => updateSection(secIdx, s => ({ ...s, modules: [...(s.modules||[]), { id: `${s.id}-m${(s.modules?.length||0)+1}`, time_limit_sec: 3600 }] }));
  const updateModule = (secIdx, modIdx, updater) => updateSection(secIdx, s => {
    const mods = [...(s.modules||[])];
    const old = { ...mods[modIdx] };
    const updated = updater(old);
    // if the module id changes, migrate its binding key and adaptive config
    if (old.id !== updated.id) {
      const oldKey = moduleKey(s.id, old.id);
      const newKey = moduleKey(s.id, updated.id);
      setModuleDrafts(prev => {
        const clone = { ...prev };
        if (clone[oldKey] && !clone[newKey]) {
          clone[newKey] = clone[oldKey];
        }
        delete clone[oldKey];
        return clone;
      });
      setAdaptiveCfg(prev => {
        const clone = { ...prev };
        if (clone[oldKey] && !clone[newKey]) {
          clone[newKey] = clone[oldKey];
        }
        delete clone[oldKey];
        return clone;
      });
    }
    mods[modIdx] = updated;
    return { ...s, modules: mods };
  });
  const removeModule = (secIdx, modIdx) => updateSection(secIdx, s => {
    const mods = (s.modules||[]);
    const removed = mods[modIdx];
    // clean bindings
    setModuleDrafts(prev => {
      const clone = { ...prev };
      delete clone[moduleKey(s.id, removed.id)];
      return clone;
    });
    setAdaptiveCfg(prev => {
      const clone = { ...prev };
      const k = moduleKey(s.id, removed.id);
      const ac = clone[k];
      if (ac?.variants?.length) {
        ac.variants.forEach(v => { delete variantDrafts[v.id]; });
      }
      delete clone[k];
      return clone;
    });
    return { ...s, modules: mods.filter((_, i) => i !== modIdx) };
  });
  
  // Scoring setters
  const setScoreItem = (secId, partKey, field, value) => {
    setScoringCfg(prev => {
      const sec = { ...(prev[secId] || {}) };
      const part = { ...(sec[partKey] || { correct: 4, wrong: -1, unattempted: 0 }) };
      part[field] = value;
      sec[partKey] = part;
      return { ...prev, [secId]: sec };
    });
  };
  const addScoringPart = (secId) => setScoringCfg(prev => {
    const sec = { ...(prev[secId] || {}) };
    const nextKey = String.fromCharCode(65 + Object.keys(sec).length); // A, B, C...
    sec[nextKey] = { correct: 4, wrong: -1, unattempted: 0 };
    return { ...prev, [secId]: sec };
  });
  const removeScoringPart = (secId, partKey) => setScoringCfg(prev => {
    const sec = { ...(prev[secId] || {}) };
    delete sec[partKey];
    return { ...prev, [secId]: sec };
  });
  
  // Question assignment helpers (single-quiz path)
  const initAssignments = (questions, defaultSection, defaultModule, defaultPart) => {
    setQAssign(questions.map(() => ({
      section_id: defaultSection,
      module_id: defaultModule,
      part: defaultPart
    })));
  };
  const setAssign = (idx, field, value) => {
    setQAssign(prev => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || {}), [field]: value };
      return next;
    });
  };
  const batchAssign = (field, value) => {
    setQAssign(prev => prev.map(a => ({ ...a, [field]: value })));
  };
  
  // Presets
  const applyJeepreset = () => {
    const secs = [
      { id: 'phy',  title: 'Physics',    modules: [{ id: 'phy-m1',  time_limit_sec: 3600 }] },
      { id: 'chem', title: 'Chemistry',  modules: [{ id: 'chem-m1', time_limit_sec: 3600 }] },
      { id: 'math', title: 'Mathematics',modules: [{ id: 'math-m1', time_limit_sec: 3600 }] },
    ];
    setSectionsCfg(secs);
    setNavCfg({ allow_back: true, module_locked: false, subject_tabs: true });
    setScoringCfg({
      phy:  { A: { correct: 4, wrong: -1, unattempted: 0 }, B: { correct: 4, wrong: 0, unattempted: 0, attempt_limit: 5, offered: 10 } },
      chem: { A: { correct: 4, wrong: -1, unattempted: 0 }, B: { correct: 4, wrong: 0, unattempted: 0, attempt_limit: 5, offered: 10 } },
      math: { A: { correct: 4, wrong: -1, unattempted: 0 }, B: { correct: 4, wrong: 0, unattempted: 0, attempt_limit: 5, offered: 10 } },
    });
    setPkgProfile('jee.v1');
    setCompositionMode('multi');
    // seed part=A for all modules (course/quiz left for teacher)
    setModuleDrafts({
      [moduleKey('phy','phy-m1')]:  { course_id: '', quiz_id: '', part: 'A' },
      [moduleKey('chem','chem-m1')]:{ course_id: '', quiz_id: '', part: 'A' },
      [moduleKey('math','math-m1')]:{ course_id: '', quiz_id: '', part: 'A' },
    });
    setAdaptiveCfg({});
    setVariantDrafts({});
  };

  const applySatPreset = () => {
    // SAT-style: 2 modules per section; module 2 is adaptive placeholder with easy/hard variants
    const secs = [
      { id: 'rw',   title: 'Reading & Writing', modules: [{ id: 'rw-m1', time_limit_sec: 2340 }, { id: 'rw-m2', time_limit_sec: 0 }] },
      { id: 'math', title: 'Math',              modules: [{ id: 'math-m1', time_limit_sec: 2580 }, { id: 'math-m2', time_limit_sec: 0 }] },
    ];
    setSectionsCfg(secs);
    setNavCfg({ allow_back: false, module_locked: true, subject_tabs: true });
    setScoringCfg({
      rw:   { A: { correct: 1, wrong: 0, unattempted: 0 } },
      math: { A: { correct: 1, wrong: 0, unattempted: 0 } },
    });
    setPkgProfile('sat.v1');
    setCompositionMode('adaptive');

    const ac = {};
    ac[moduleKey('rw','rw-m2')]     = { enabled: true, threshold: 18, variants: [
      { id: 'rw-m2-easy', time_limit_sec: 1800 },
      { id: 'rw-m2-hard', time_limit_sec: 1800 }
    ]};
    ac[moduleKey('math','math-m2')] = { enabled: true, threshold: 20, variants: [
      { id: 'math-m2-easy', time_limit_sec: 1800 },
      { id: 'math-m2-hard', time_limit_sec: 1800 }
    ]};
    setAdaptiveCfg(ac);

    // Stage-1 modules use regular per-module binding (teacher picks)
    setModuleDrafts({
      [moduleKey('rw','rw-m1')]:     { course_id: '', quiz_id: '', part: 'A' },
      [moduleKey('math','math-m1')]: { course_id: '', quiz_id: '', part: 'A' },
    });

    // Stage-2 variants: seed empty variant draft selections (teacher picks later)
    setVariantDrafts({
      'rw-m2-easy':   { course_id: '', quiz_id: '', part: 'A' },
      'rw-m2-hard':   { course_id: '', quiz_id: '', part: 'A' },
      'math-m2-easy': { course_id: '', quiz_id: '', part: 'A' },
      'math-m2-hard': { course_id: '', quiz_id: '', part: 'A' }
    });
  };

  // ── Effects for Single mode only ────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === 0 && compositionMode === 'single') {
      setError('');
      if (selectedCourse) fetchDownloadQuizzes();
      else {
        setDownloadQuizzes([]);
        setSelectedDownloadQuizId('');
      }
    }
  }, [activeTab, selectedCourse, compositionMode]);

  useEffect(() => {
    const load = async () => {
      setError('');
      setDlQuizSpec(null);
      if (compositionMode !== 'single') return;
      if (!selectedCourse || !selectedDownloadQuizId) return;
      try {
        const resp = await axiosInstance.get(`/exams/quiz/${selectedDownloadQuizId}`);
        const data = resp.data || {};
        const qlist = data?.spec?.questions || [];
  
        setDlQuizSpec(data);
        // default package ID/title from quiz name
        setPkgId((data.name || 'quiz').toLowerCase().replace(/[^a-z0-9-_]+/gi, '-'));
        setPkgTitle(data.name || 'Quiz');
  
        // seed assignments to first section/module if present; else ensure sectionsCfg has main/main-m1
        const sec0 = sectionsCfg[0]?.id || 'main';
        const mod0 = sectionsCfg[0]?.modules?.[0]?.id || 'main-m1';
        initAssignments(qlist, sec0, mod0, PART_DEFAULT);
      } catch {
        setError('Unable to load quiz spec.');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDownloadQuizId, compositionMode]);

  // Helpers to toggle adaptive UI for a module
  const toggleAdaptiveFor = (secId, modId, on) => {
    const key = moduleKey(secId, modId);
    setAdaptiveCfg(prev => {
      const next = { ...prev };
      if (on) {
        if (!next[key]) {
          next[key] = {
            enabled: true,
            threshold: 18,
            variants: [
              { id: `${modId}-easy`, time_limit_sec: 1800 },
              { id: `${modId}-hard`, time_limit_sec: 1800 }
            ]
          };
        } else {
          next[key] = { ...next[key], enabled: true };
        }
      } else {
        // clean variant draft selections tied to this module
        const vlist = next[key]?.variants || [];
        setVariantDrafts(prevVD => {
          const clone = { ...prevVD };
          vlist.forEach(v => delete clone[v.id]);
          return clone;
        });
        next[key] = { ...(next[key] || {}), enabled: false };
      }
      return next;
    });
  };

  // UI color cues per mode
  const modeBg = compositionMode === 'single' ? '#f1f8e9' : compositionMode === 'multi' ? '#fff8e1' : '#e3f2fd';
  const modeBorder = compositionMode === 'single' ? '#c5e1a5' : compositionMode === 'multi' ? '#ffe082' : '#90caf9';

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
        >
          <Tab label="Quiz → Download" />
        </Tabs>

        {/* ── Quiz → Download (single tab) ─────────────────────────────────────────── */}
        {activeTab === 0 && (
  <>
    <Typography variant="h5" gutterBottom>
      Quiz → Download
    </Typography>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

    {/* Composition mode banner (three options with color cues) */}
    <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: modeBg, border: '1px solid', borderColor: modeBorder }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Composition Mode
      </Typography>
      <RadioGroup
        row
        value={compositionMode}
        onChange={(e) => setCompositionMode(e.target.value)}
      >
        <FormControlLabel value="single"   control={<Radio />} label="Single quiz (Single module)" />
        <FormControlLabel value="multi"    control={<Radio />} label="Multi modules" />
        <FormControlLabel value="adaptive" control={<Radio />} label="Adaptive multi module (SAT)" />
      </RadioGroup>
      <FormHelperText>
        {compositionMode === 'single'
          ? 'Pick one quiz and map its questions to your section/module.'
          : compositionMode === 'multi'
            ? 'Bind each module to a quiz draft. Teacher ensures counts/types match.'
            : 'Define placeholders that branch to easy/hard variants; bind a quiz to each branch. Router uses by_score threshold.'}
      </FormHelperText>
    </Box>

    {/* Package header */}
    <Typography variant="h6" gutterBottom>Exam</Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
      <TextField
        label="Exam ID"
        value={pkgId}
        onChange={e => setPkgId(e.target.value)}
        helperText="e.g., jee-main-p1-001"
        fullWidth
      />
      <TextField
        label="Title"
        value={pkgTitle}
        onChange={e => setPkgTitle(e.target.value)}
        helperText="Display name"
        fullWidth
      />
      <FormControl fullWidth>
        <InputLabel id="profile">Profile</InputLabel>
        <Select
          labelId="profile"
          value={pkgProfile}
          label="Profile"
          onChange={e => setPkgProfile(e.target.value)}
        >
          <MenuItem value="stem.v1">stem.v1</MenuItem>
          <MenuItem value="jee.v1">jee.v1</MenuItem>
          <MenuItem value="sat.v1">sat.v1</MenuItem>
        </Select>
      </FormControl>
    </Stack>

    {/* Presets */}
    <Box sx={{ mb: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <Button size="small" onClick={applyJeepreset}>Quick Preset: JEE (Multi)</Button>
        <Button size="small" onClick={applySatPreset}>Quick Preset: SAT Adaptive</Button>
      </Stack>
    </Box>

    {/* Sections & Modules */}
    <Typography variant="h6" gutterBottom>Sections & Modules</Typography>
    {sectionsCfg.map((s, si) => (
      <Paper key={si} variant="outlined" sx={{ p: 2, mb: 2, borderLeft: '4px solid #90caf9', bgcolor: '#f3f8fe' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }}>
          <TextField
            label="Section ID"
            value={s.id}
            onChange={e => updateSection(si, old => ({ ...old, id: e.target.value }))}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Section Title"
            value={s.title}
            onChange={e => updateSection(si, old => ({ ...old, title: e.target.value }))}
            sx={{ flex: 1 }}
          />
          <Button color="error" onClick={() => removeSection(si)} disabled={sectionsCfg.length <= 1}>
            Remove Section
          </Button>
        </Stack>

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Modules</Typography>
        {(s.modules||[]).map((m, mi) => {
          const key = moduleKey(s.id, m.id);
          const bind = moduleDrafts[key] || {};
          const ac = adaptiveCfg[key] || {};
          const courseQuizzes = bind.course_id ? (quizzesByCourse[bind.course_id] || []) : [];
          return (
            <Box key={mi} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px dashed #bbdefb', bgcolor: '#ffffff' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }}>
                <TextField
                  label="Module ID"
                  value={m.id}
                  onChange={e => updateModule(si, mi, old => ({ ...old, id: e.target.value }))}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  label="Time Limit (sec)"
                  type="number"
                  value={m.time_limit_sec}
                  onChange={e => updateModule(si, mi, old => ({ ...old, time_limit_sec: Number(e.target.value) }))}
                  sx={{ minWidth: 220 }}
                />
                <Button color="error" onClick={() => removeModule(si, mi)} disabled={(s.modules||[]).length <= 1}>
                  Remove Module
                </Button>
              </Stack>

              {/* MULTI / ADAPTIVE UI */}
              {compositionMode !== 'single' ? (
                <>
                  {/* Adaptive placeholder toggle (only visible in adaptive mode) */}
                  {compositionMode === 'adaptive' && (
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: ac.enabled ? '#e3f2fd' : '#f9fbe7', border: '1px solid', borderColor: ac.enabled ? '#90caf9' : '#dce775', mb: 2 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!ac.enabled}
                              onChange={e => toggleAdaptiveFor(s.id, m.id, e.target.checked)}
                            />
                          }
                          label="Adaptive placeholder (route to easy/hard next)"
                        />
                        {ac.enabled && (
                          <TextField
                            label="Score Threshold"
                            type="number"
                            value={ac.threshold ?? 18}
                            onChange={e => setAdaptiveCfg(prev => {
                              const clone = { ...prev };
                              clone[key] = { ...(clone[key] || {}), enabled: true, threshold: Number(e.target.value), variants: clone[key]?.variants || [
                                { id: `${m.id}-easy`, time_limit_sec: 1800 },
                                { id: `${m.id}-hard`, time_limit_sec: 1800 }
                              ] };
                              return clone;
                            })}
                            sx={{ width: 200 }}
                            helperText="by_score: lte → easy, gt → hard"
                          />
                        )}
                      </Stack>

                      {/* Variants editing + per-variant draft pickers */}
                      {ac.enabled && (
                        <Box sx={{ mt: 2 }}>
                          {(ac.variants || []).map((v, vidx) => {
                            const vId = v.id || '';
                            const vd = variantDrafts[vId] || {};
                            const vCourseQuizzes = vd.course_id ? (quizzesByCourse[vd.course_id] || []) : [];
                            return (
                              <Paper key={vidx} variant="outlined" sx={{ p: 2, mb: 1, borderLeft: '4px solid #64b5f6', bgcolor: '#f7fbff' }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  <TextField
                                    label={`Variant ID ${vidx+1}`}
                                    value={vId}
                                    onChange={e => setAdaptiveCfg(prev => {
                                      const clone = { ...prev };
                                      const arr = [...(clone[key]?.variants || [])];
                                      arr[vidx] = { ...(arr[vidx] || {}), id: e.target.value };
                                      clone[key] = { ...(clone[key] || {}), variants: arr, enabled: true, threshold: (clone[key]?.threshold ?? 18) };
                                      return clone;
                                    })}
                                    sx={{ minWidth: 220 }}
                                    helperText={vidx === 0 ? 'Easy (LTE threshold)' : (vidx === 1 ? 'Hard (GT threshold)' : 'Default (optional)')}
                                  />
                                  <TextField
                                    label="Time Limit (sec)"
                                    type="number"
                                    value={v.time_limit_sec ?? 1800}
                                    onChange={e => setAdaptiveCfg(prev => {
                                      const clone = { ...prev };
                                      const arr = [...(clone[key]?.variants || [])];
                                      arr[vidx] = { ...(arr[vidx] || {}), time_limit_sec: Number(e.target.value) };
                                      clone[key] = { ...(clone[key] || {}), variants: arr };
                                      return clone;
                                    })}
                                    sx={{ width: 200 }}
                                  />
                                </Stack>

                                {/* Per-variant draft mapping */}
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
                                  <FormControl sx={{ minWidth: 220 }}>
                                    <InputLabel id={`var-course-${s.id}-${m.id}-${vidx}`}>Course for this variant</InputLabel>
                                    <Select
                                      labelId={`var-course-${s.id}-${m.id}-${vidx}`}
                                      value={vd.course_id || ''}
                                      label="Course for this variant"
                                      onChange={async (e) => {
                                        const val = e.target.value;
                                        setVariantDrafts(prev => ({
                                          ...prev,
                                          [vId]: { ...(prev[vId] || {}), course_id: val, quiz_id: '' }
                                        }));
                                        await fetchQuizzesForCourse(val);
                                      }}
                                    >
                                      <MenuItem value="">— Pick a course —</MenuItem>
                                      {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.subject}</MenuItem>)}
                                    </Select>
                                  </FormControl>

                                  <FormControl sx={{ minWidth: 260 }} disabled={!vd.course_id}>
                                    <InputLabel id={`var-quiz-${s.id}-${m.id}-${vidx}`}>Quiz draft for this variant</InputLabel>
                                    <Select
                                      labelId={`var-quiz-${s.id}-${m.id}-${vidx}`}
                                      value={vd.quiz_id || ''}
                                      label="Quiz draft for this variant"
                                      onChange={e => setVariantDrafts(prev => ({
                                        ...prev,
                                        [vId]: { ...(prev[vId] || {}), quiz_id: e.target.value }
                                      }))}
                                    >
                                      <MenuItem value="">— Pick a quiz —</MenuItem>
                                      {(vd.course_id ? (quizzesByCourse[vd.course_id] || []) : []).map(q => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                                    </Select>
                                    <FormHelperText>Questions for this branch come from the selected draft.</FormHelperText>
                                  </FormControl>

                                  <TextField
                                    label="Part"
                                    value={vd.part ?? PART_DEFAULT}
                                    onChange={e => setVariantDrafts(prev => ({
                                      ...prev,
                                      [vId]: { ...(prev[vId] || {}), part: e.target.value }
                                    }))}
                                    sx={{ width: 120 }}
                                    helperText="e.g., A / B"
                                  />
                                </Stack>
                              </Paper>
                            );
                          })}
                          <Box>
                            <Button
                              size="small"
                              onClick={() => setAdaptiveCfg(prev => {
                                const clone = { ...prev };
                                const arr = [...(clone[key]?.variants || [])];
                                arr.push({ id: `${m.id}-alt-${arr.length+1}`, time_limit_sec: 1800 });
                                clone[key] = { ...(clone[key] || {}), variants: arr, enabled: true, threshold: (clone[key]?.threshold ?? 18) };
                                return clone;
                              })}
                            >
                              Add Variant
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Non-adaptive per-module binding (shown in MULTI mode, or in ADAPTIVE when placeholder not enabled) */}
                  {(compositionMode === 'multi' || !ac.enabled) && (
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Module draft</Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl sx={{ minWidth: 220 }}>
                          <InputLabel id={`mod-course-${s.id}-${m.id}`}>Course for this module</InputLabel>
                          <Select
                            labelId={`mod-course-${s.id}-${m.id}`}
                            value={bind.course_id || ''}
                            label="Course for this module"
                            onChange={async (e) => {
                              const val = e.target.value;
                              setModuleDrafts(prev => ({
                                ...prev,
                                [moduleKey(s.id, m.id)]: { ...(prev[moduleKey(s.id, m.id)] || {}), course_id: val, quiz_id: '' }
                              }));
                              await fetchQuizzesForCourse(val);
                            }}
                          >
                            <MenuItem value="">— Pick a course —</MenuItem>
                            {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.subject}</MenuItem>)}
                          </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 260 }} disabled={!bind.course_id}>
                          <InputLabel id={`mod-quiz-${s.id}-${m.id}`}>Quiz draft for this module</InputLabel>
                          <Select
                            labelId={`mod-quiz-${s.id}-${m.id}`}
                            value={bind.quiz_id || ''}
                            label="Quiz draft for this module"
                            onChange={e => setModuleDrafts(prev => ({
                              ...prev,
                              [moduleKey(s.id, m.id)]: { ...(prev[moduleKey(s.id, m.id)] || {}), quiz_id: e.target.value }
                            }))}
                          >
                            <MenuItem value="">— Pick a quiz —</MenuItem>
                            {courseQuizzes.map(q => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                          </Select>
                          <FormHelperText>All questions in this module will come from this draft.</FormHelperText>
                        </FormControl>

                        <TextField
                          label="Part"
                          value={bind.part ?? PART_DEFAULT}
                          onChange={e => setModuleDrafts(prev => ({
                            ...prev,
                            [moduleKey(s.id, m.id)]: { ...(prev[moduleKey(s.id, m.id)] || {}), part: e.target.value }
                          }))}
                          sx={{ width: 120 }}
                          helperText="e.g., A / B"
                        />

                        <Button
                          onClick={() => setModuleDrafts(prev => {
                            const clone = { ...prev };
                            delete clone[moduleKey(s.id, m.id)];
                            return clone;
                          })}
                        >
                          Clear selection
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="success" sx={{ my: 1 }}>
                  Single mode active: use the single quiz selector below and map questions to sections/modules.
                </Alert>
              )}
            </Box>
          );
        })}
        <Button variant="outlined" size="small" onClick={() => addModule(si)}>Add Module</Button>
      </Paper>
    ))}
    <Button variant="outlined" size="small" sx={{ mb: 2 }} onClick={addSection}>Add Section</Button>

    {/* Navigation */}
    <Typography variant="h6" gutterBottom>Navigation</Typography>
    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
      <FormControlLabel
        control={<Radio checked={navCfg.allow_back} onChange={() => setNavCfg(n => ({ ...n, allow_back: true }))} />}
        label="Allow Back (Yes)"
      />
      <FormControlLabel
        control={<Radio checked={!navCfg.allow_back} onChange={() => setNavCfg(n => ({ ...n, allow_back: false }))} />}
        label="Allow Back (No)"
      />
      <FormControlLabel
        control={<Checkbox checked={navCfg.module_locked} onChange={e => setNavCfg(n => ({ ...n, module_locked: e.target.checked }))} />}
        label="Module Locked"
      />
      <FormControlLabel
        control={<Checkbox checked={navCfg.subject_tabs} onChange={e => setNavCfg(n => ({ ...n, subject_tabs: e.target.checked }))} />}
        label="Subject Tabs"
      />
    </Stack>

    {/* Scoring */}
    <Typography variant="h6" gutterBottom>Scoring</Typography>
    {sectionsCfg.map(s => {
      const secScore = scoringCfg[s.id] || {};
      const parts = Object.keys(secScore);
      return (
        <Paper key={s.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{s.title} ({s.id})</Typography>
          {!!parts.length && parts.map(pk => {
            const row = secScore[pk] || {};
            return (
              <Stack key={pk} direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }}>
                <TextField label="Part" value={pk} disabled sx={{ width: 80 }} />
                <TextField label="Correct" type="number" value={row.correct ?? 0} onChange={e => setScoreItem(s.id, pk, 'correct', Number(e.target.value))} sx={{ width: 140 }} />
                <TextField label="Wrong" type="number" value={row.wrong ?? 0} onChange={e => setScoreItem(s.id, pk, 'wrong', Number(e.target.value))} sx={{ width: 140 }} />
                <TextField label="Unattempted" type="number" value={row.unattempted ?? 0} onChange={e => setScoreItem(s.id, pk, 'unattempted', Number(e.target.value))} sx={{ width: 160 }} />
                <TextField label="Attempt Limit (opt)" type="number" value={row.attempt_limit ?? ''} onChange={e => setScoreItem(s.id, pk, 'attempt_limit', e.target.value === '' ? undefined : Number(e.target.value))} sx={{ width: 180 }} />
                <TextField label="Offered (opt)" type="number" value={row.offered ?? ''} onChange={e => setScoreItem(s.id, pk, 'offered', e.target.value === '' ? undefined : Number(e.target.value))} sx={{ width: 160 }} />
                <Button color="error" onClick={() => removeScoringPart(s.id, pk)}>Remove</Button>
              </Stack>
            );
          })}
          <Button variant="outlined" size="small" onClick={() => addScoringPart(s.id)}>Add Part</Button>
        </Paper>
      );
    })}

    {/* Proctor & Meta */}
    <Typography variant="h6" gutterBottom>Proctor & Meta</Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
      <FormControlLabel
        control={<Checkbox checked={proctorCfg.screenshot} onChange={e => setProctorCfg(p => ({ ...p, screenshot: e.target.checked }))} />}
        label="Screenshot"
      />
      <FormControlLabel
        control={<Checkbox checked={proctorCfg.camera} onChange={e => setProctorCfg(p => ({ ...p, camera: e.target.checked }))} />}
        label="Camera"
      />
      <TextField
        label="Meta Version"
        value={metaVersion}
        onChange={e => setMetaVersion(e.target.value)}
      />
    </Stack>

    {/* Single-quiz path (only when compositionMode === 'single') */}
    {compositionMode === 'single' && (
      <>
        <Divider sx={{ my: 2 }} />
        {/* Course/Quiz Pickers */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="dl-course-label">Select Course</InputLabel>
          <Select
            labelId="dl-course-label"
            value={selectedCourse}
            label="Select Course"
            onChange={e => setSelectedCourse(e.target.value)}
          >
            <MenuItem value="">-- Select a Course --</MenuItem>
            {courses.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.subject}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="dl-quiz-select-label">Select Quiz</InputLabel>
          <Select
            labelId="dl-quiz-select-label"
            value={selectedDownloadQuizId}
            label="Select Quiz"
            onChange={e => setSelectedDownloadQuizId(e.target.value)}
            disabled={!selectedCourse || downloadQuizzes.length === 0}
          >
            <MenuItem value="">-- Select a Quiz --</MenuItem>
            {downloadQuizzes.map(q => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Start with a draft quiz, then add policy & format below to export a complete exam.
          </FormHelperText>
        </FormControl>

        {/* Mapping only when quiz spec is loaded */}
        {!!dlQuizSpec?.spec?.questions?.length && (
          <>
            {/* Question Mapping */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Question Mapping</Typography>

            {/* Batch defaults */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel id="batch-sec">Default Section</InputLabel>
                <Select
                  labelId="batch-sec"
                  label="Default Section"
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    batchAssign('section_id', val);
                    const sec = sectionsCfg.find(s => s.id === val);
                    if (sec?.modules?.[0]?.id) batchAssign('module_id', sec.modules[0].id);
                  }}
                >
                  {sectionsCfg.map(s => <MenuItem key={s.id} value={s.id}>{s.title} ({s.id})</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel id="batch-part">Default Part</InputLabel>
                <Select
                  labelId="batch-part"
                  label="Default Part"
                  value=""
                  onChange={(e) => batchAssign('part', e.target.value)}
                >
                  {['A','B','C','D','E'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            {/* Per-question rows */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              {dlQuizSpec.spec.questions.map((q, i) => {
                const assign = qAssign[i] || {};
                const sec = sectionsCfg.find(s => s.id === assign.section_id) || sectionsCfg[0];
                const modules = sec?.modules || [];
                return (
                  <Paper key={q.id || i} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {`Q${i+1}`} • {q.type} • {q.id || '(no id)'}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl sx={{ minWidth: 220 }}>
                        <InputLabel id={`sec-${i}`}>Section</InputLabel>
                        <Select
                          labelId={`sec-${i}`}
                          label="Section"
                          value={assign.section_id || sec?.id || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAssign(i, 'section_id', val);
                            const s = sectionsCfg.find(s => s.id === val);
                            const m0 = s?.modules?.[0]?.id || '';
                            setAssign(i, 'module_id', m0);
                          }}
                        >
                          {sectionsCfg.map(s => <MenuItem key={s.id} value={s.id}>{s.title} ({s.id})</MenuItem>)}
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 220 }}>
                        <InputLabel id={`mod-${i}`}>Module</InputLabel>
                        <Select
                          labelId={`mod-${i}`}
                          label="Module"
                          value={assign.module_id || modules[0]?.id || ''}
                          onChange={(e) => setAssign(i, 'module_id', e.target.value)}
                        >
                          {modules.map(m => <MenuItem key={m.id} value={m.id}>{m.id}</MenuItem>)}
                        </Select>
                      </FormControl>

                      <TextField
                        label="Part"
                        value={assign.part || PART_DEFAULT}
                        onChange={e => setAssign(i, 'part', e.target.value)}
                        sx={{ width: 120 }}
                        helperText="e.g., A / B"
                      />

                      <TextField
                        label="Points (optional override)"
                        type="number"
                        value={assign.points ?? ''}
                        onChange={e => setAssign(i, 'points', e.target.value === '' ? '' : Number(e.target.value))}
                        sx={{ width: 220 }}
                      />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </>
        )}

        {!dlQuizSpec?.spec?.questions?.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Select a quiz above to configure mapping before download.
          </Alert>
        )}
      </>
    )}

    <Divider sx={{ my: 2 }} />
    <Button
      variant="contained"
      fullWidth
      onClick={handleDownloadQuiz}
      startIcon={downloading && <CircularProgress size={20} />}
      disabled={downloading}
    >
      {downloading ? 'Preparing…' : 'Download Exam JSON'}
    </Button>
  </>
)}
      </Paper>
    </Container>
  );
}

